import React, { useState } from "react";
import {
  FaShoppingCart,
  FaCalendarAlt,
  FaWallet,
  FaHandHoldingUsd,
  FaBoxes,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaReceipt,
} from "react-icons/fa";

// ─── Default mock data ───────────────────────────────────────────────────────
const DEFAULT_DATA = {
  businessName: "",
  todaySales: 48500,
  todaySalesChange: 12.4,
  monthlySales: 1284000,
  monthlySalesChange: 8.2,
  cashAndBank: 2450000,
  receivables: 1240000,
  inventoryValue: 890000,
  payables: 670000,
  pnl: {
    revenue: 2850000,
    cogs: 1420000,
    grossProfit: 1430000,
    netProfit: 930000,
  },
  aging: [
    { label: "Current", amount: 420000 },
    { label: "1–30", amount: 310000 },
    { label: "31–60", amount: 180000 },
    { label: "60+", amount: 330000 },
  ],
  inventoryAlerts: [
    {
      type: "low",
      count: 12,
      label: "Low Stock",
      color: "text-rose-400",
    },
    {
      type: "reorder",
      count: 8,
      label: "Reorder",
      color: "text-amber-400",
    },
    {
      type: "expiring",
      count: 5,
      label: "Expiring",
      color: "text-yellow-400",
    },
  ],
  topProducts: [
    { name: "Coca Cola", qty: 1245 },
    { name: "Bread", qty: 982 },
    { name: "Water", qty: 845 },
  ],
  tax: {
    outputVat: 427000,
    inputVat: 286000,
    vatPayable: 141000,
  },
  recentTx: [
    {
      id: 1,
      ref: "INV-1042",
      party: "Abebe Trading",
      type: "Sale",
      amount: 12500,
      date: "2026-08-23T10:22:00",
    },
    {
      id: 2,
      ref: "BILL-881",
      party: "Habesha Suppliers",
      type: "Purchase",
      amount: -8400,
      date: "2026-08-23T09:15:00",
    },
    {
      id: 3,
      ref: "INV-1041",
      party: "Selam Store",
      type: "Sale",
      amount: 3200,
      date: "2026-08-22T16:40:00",
    },
    {
      id: 4,
      ref: "RCP-552",
      party: "Cash Sale",
      type: "Sale",
      amount: 1800,
      date: "2026-08-22T14:05:00",
    },
    {
      id: 5,
      ref: "BILL-880",
      party: "Addis Distributors",
      type: "Purchase",
      amount: -15600,
      date: "2026-08-22T11:30:00",
    },
  ],
  salesTrend: [42, 55, 48, 62, 58, 71, 65, 80, 74, 88, 82, 95],
  revenueVsExpense: {
    labels: ["Mar", "Apr", "May", "Jun", "Jul", "Aug"],
    revenue: [210, 240, 255, 270, 290, 285],
    expense: [140, 155, 160, 170, 180, 175],
  },
};

function formatCurrency(value) {
  const num = Number(value || 0);

  if (Math.abs(num) >= 1_000_000) {
    return `ETB ${(num / 1_000_000).toFixed(2)}M`;
  }

  if (Math.abs(num) >= 1_000) {
    return `ETB ${(num / 1_000).toFixed(0)}K`;
  }

  return `ETB ${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatFullCurrency(value) {
  return `ETB ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getGreeting() {
  const h = new Date().getHours();

  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";

  return "Good evening";
}

function formatToday() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function LineChart({ data, height = 120 }) {
  const max = Math.max(...data, 1);
  const w = 320;
  const h = height;
  const stepX = w / (data.length - 1 || 1);

  const points = data
    .map((v, i) => `${i * stepX},${h - (v / max) * (h - 16) - 8}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-28"
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
        className="text-emerald-500"
      />

      {data.map((v, i) => (
        <circle
          key={i}
          cx={i * stepX}
          cy={h - (v / max) * (h - 16) - 8}
          r="3"
          className="fill-emerald-500"
        />
      ))}
    </svg>
  );
}

function BarCompare({ revenue, expense, labels }) {
  const max = Math.max(...revenue, ...expense, 1);
  const barW = 18;
  const gap = 28;
  const h = 110;

  return (
    <svg
      viewBox={`0 0 ${labels.length * gap + 20} ${h + 24}`}
      className="w-full h-32"
    >
      {labels.map((label, i) => {
        const x = 12 + i * gap;
        const rH = (revenue[i] / max) * h;
        const eH = (expense[i] / max) * h;

        return (
          <g key={label}>
            <rect
              x={x}
              y={h - rH}
              width={barW / 2 - 1}
              height={rH}
              rx="2"
              className="fill-emerald-500"
            />

            <rect
              x={x + barW / 2 + 1}
              y={h - eH}
              width={barW / 2 - 1}
              height={eH}
              rx="2"
              className="fill-rose-400"
            />

            <text
              x={x + barW / 2}
              y={h + 14}
              textAnchor="middle"
              className="fill-slate-400 text-[8px]"
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * DashboardAd
 * Priority: dataProp → DEFAULT_DATA
 */
export default function DashboardAd({ data: dataProp, className = "" }) {
  const [trendRange, setTrendRange] = useState("30D");

  const rangeBtns = ["7D", "30D", "3M", "12M"];

  // Use prop data if provided, otherwise fall back to dummy data
  const data = dataProp || DEFAULT_DATA;

  const kpiCards = [
    {
      title: "Today's Sales",
      value: formatCurrency(data.todaySales),
      change: data.todaySalesChange,
      icon: <FaShoppingCart />,
      color: "text-emerald-400",
      bg: "bg-emerald-500/10",
    },
    {
      title: "Monthly Sales",
      value: formatCurrency(data.monthlySales),
      change: data.monthlySalesChange,
      icon: <FaCalendarAlt />,
      color: "text-blue-400",
      bg: "bg-blue-500/10",
    },
    {
      title: "Cash & Bank",
      value: formatCurrency(data.cashAndBank),
      icon: <FaWallet />,
      color: "text-purple-400",
      bg: "bg-purple-500/10",
    },
    {
      title: "Receivables",
      value: formatCurrency(data.receivables),
      icon: <FaHandHoldingUsd />,
      color: "text-amber-400",
      bg: "bg-amber-500/10",
    },
    {
      title: "Inventory",
      value: formatCurrency(data.inventoryValue),
      icon: <FaBoxes />,
      color: "text-cyan-400",
      bg: "bg-cyan-500/10",
    },
    {
      title: "Payables",
      value: formatCurrency(data.payables),
      icon: <FaFileInvoiceDollar />,
      color: "text-rose-400",
      bg: "bg-rose-500/10",
    },
  ];

  return (
    <div
      className={`min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-950 text-slate-100 ${className}`}
    >
      {/* Background glow */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/10 blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/10 blur-[150px]" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-2xl border shadow-xl backdrop-blur-xl bg-slate-900/40 border-white/10">
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold text-white">
              {getGreeting()}
              {data.businessName ? `, ${data.businessName}` : ""}
            </h1>

            <p className="text-xs mt-0.5 text-slate-400">{formatToday()}</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpiCards.map((k) => (
            <div
              key={k.title}
              className="p-4 rounded-2xl border backdrop-blur-xl bg-slate-900/30 border-white/10 shadow-2xl"
            >
              <div
                className={`inline-flex p-2 rounded-lg mb-2 ${k.bg} ${k.color}`}
              >
                {k.icon}
              </div>

              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                {k.title}
              </p>

              <p className={`text-lg font-bold mt-0.5 ${k.color}`}>{k.value}</p>

              {k.change != null && (
                <p
                  className={`text-[11px] mt-1 flex items-center gap-1 font-medium ${
                    k.change >= 0 ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {k.change >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                  {Math.abs(k.change)}%
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Sales Trend */}
        <div className="p-5 rounded-2xl border shadow-2xl backdrop-blur-xl bg-slate-900/30 border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200">
              Sales Trend
            </h2>

            <div className="flex gap-1 p-1 rounded-xl bg-slate-950/60 border border-white/10">
              {rangeBtns.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setTrendRange(r)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition ${
                    trendRange === r
                      ? "bg-emerald-600 text-white shadow"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <LineChart data={data.salesTrend || []} />
        </div>

        {/* Revenue vs Expenses + P&L */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Revenue vs Expenses */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-3">
              Revenue vs Expenses
            </h2>

            <div className="flex items-center gap-4 mb-2 text-[10px] text-slate-300">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                Revenue
              </span>

              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-rose-400" />
                Expenses
              </span>
            </div>

            <BarCompare
              revenue={data.revenueVsExpense?.revenue || []}
              expense={data.revenueVsExpense?.expense || []}
              labels={data.revenueVsExpense?.labels || []}
            />
          </div>

          {/* Profit & Loss */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
              Profit & Loss
            </h2>

            <div className="space-y-3">
              {[
                {
                  label: "Revenue",
                  value: data.pnl?.revenue,
                  color: "text-emerald-400",
                },
                {
                  label: "COGS",
                  value: data.pnl?.cogs,
                  color: "text-rose-400",
                },
                {
                  label: "Gross Profit",
                  value: data.pnl?.grossProfit,
                  color: "text-blue-400",
                },
                {
                  label: "Net Profit",
                  value: data.pnl?.netProfit,
                  color: "text-emerald-300 font-extrabold",
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex justify-between items-center py-2 border-b border-white/5 last:border-0"
                >
                  <span className="text-sm text-slate-300">{row.label}</span>

                  <span className={`font-mono font-bold text-sm ${row.color}`}>
                    {formatCurrency(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Aging + Inventory Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Receivables Aging */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
              Receivables Aging
            </h2>

            <div className="space-y-2.5">
              {(data.aging || []).map((a) => (
                <div
                  key={a.label}
                  className="flex justify-between items-center"
                >
                  <span className="text-sm text-slate-300">{a.label}</span>

                  <span className="font-mono font-semibold text-sm text-slate-100">
                    {formatCurrency(a.amount)}
                  </span>
                </div>
              ))}

              <div className="pt-2 mt-2 border-t border-white/10 flex justify-between font-bold">
                <span className="text-xs uppercase text-slate-400">
                  Total AR
                </span>

                <span className="font-mono text-amber-400">
                  {formatCurrency(
                    (data.aging || []).reduce(
                      (s, a) => s + Number(a.amount || 0),
                      0
                    )
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Inventory Alerts */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
              Inventory Alerts
            </h2>

            <div className="space-y-3">
              {(data.inventoryAlerts || []).map((a) => (
                <div
                  key={a.type}
                  className="flex items-center justify-between p-3 rounded-xl border bg-slate-950/40 border-white/10"
                >
                  <div className="flex items-center gap-2">
                    <FaExclamationTriangle className={a.color} />

                    <span className="text-sm font-medium text-slate-200">
                      {a.label}
                    </span>
                  </div>

                  <span className={`text-lg font-bold font-mono ${a.color}`}>
                    {a.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Products + Tax */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top Products */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
              Top Products
            </h2>

            <div className="space-y-2.5">
              {(data.topProducts || []).map((p, i) => (
                <div
                  key={p.name}
                  className="flex justify-between items-center py-2 border-b border-white/5 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-slate-400">
                      {i + 1}
                    </span>

                    <span className="text-sm font-medium text-slate-100">
                      {p.name}
                    </span>
                  </div>

                  <span className="font-mono text-sm text-slate-300">
                    {Number(p.qty || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Tax Summary */}
          <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
              Tax Summary
            </h2>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-300">Output VAT</span>

                <span className="font-mono font-semibold text-sm text-slate-100">
                  {formatCurrency(data.tax?.outputVat)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-slate-300">Input VAT</span>

                <span className="font-mono font-semibold text-sm text-emerald-400">
                  {formatCurrency(data.tax?.inputVat)}
                </span>
              </div>

              <div className="pt-2 border-t border-white/10 flex justify-between font-bold">
                <span className="text-sm text-slate-200">VAT Payable</span>

                <span className="font-mono text-rose-400">
                  {formatCurrency(data.tax?.vatPayable)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="p-5 rounded-2xl border shadow-2xl bg-slate-900/30 border-white/10">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-4">
            Recent Transactions
          </h2>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider bg-slate-950/50 text-slate-400 border-b border-white/10">
                  <th className="p-3">Date</th>
                  <th className="p-3">Reference</th>
                  <th className="p-3">Party</th>
                  <th className="p-3">Type</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/5">
                {(data.recentTx || []).map((tx) => (
                  <tr key={tx.id} className="hover:bg-white/[0.02] transition">
                    <td className="p-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                      {new Date(tx.date).toLocaleString()}
                    </td>

                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-slate-200">
                        <FaReceipt className="text-slate-500 text-xs" />
                        {tx.ref}
                      </span>
                    </td>

                    <td className="p-3 text-slate-300">{tx.party}</td>

                    <td className="p-3">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                          tx.type === "Sale"
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                            : "bg-rose-500/10 text-rose-400 border-rose-500/20"
                        }`}
                      >
                        {tx.type}
                      </span>
                    </td>

                    <td
                      className={`p-3 text-right font-mono font-bold ${
                        tx.amount >= 0 ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {formatFullCurrency(tx.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
